import React, {useState, useEffect, useCallback, useRef} from 'react';
import {withTranslation} from '../../ChildrenContext.js';
import PropTypes from 'prop-types';
import CustomModal from '../../Modal';
import FormGroup from '../../FormGroup';
import CustomNotify from "../../Notify";
import ClipboardCopy from '../../../helpers/ClipboardCopy';
import config, {statuses, metaUtmParams} from '../../../config';
import Button from "../../Button";
import Collapse, {Panel} from 'rc-collapse';
import Icon from '../../Icon';
import CustomTooltip from '../../Tooltip';

const pickFirstGoalId = (goalEntries = []) => {
    if (!Array.isArray(goalEntries) || !goalEntries.length) {
        return null;
    }
    const goal = goalEntries.find(g => g.access_granted) || goalEntries[0];
    return goal?.id || null;
};

const ConstructorLandings = ({
                                 visible: propVisible,
                                 landing: propLanding,
                                 goalEntries: propGoalEntries,
                                 publisherId: propPublisherId,
                                 publisherIsReferral: propPublisherIsReferral,
                                 offer: propOffer,
                                 links: propLinks,
                                 saveLanding: propSaveLanding,
                                 onClose: propOnClose,
                                 t
                             }) => {
    const [state, setState] = useState({
        visible: propVisible,
        landing: propLanding,
        goalEntries: propGoalEntries,
        publisherId: propPublisherId,
        publisherIsReferral: propPublisherIsReferral,
        offer: propOffer,
        metaParams: {},
        currentLink: {},
        links: Array.isArray(propLinks) ? propLinks : [],
        selfieLink: '',
        link: '',
        saveLanding: propSaveLanding,
        creativeType: 'landing',
        creativeId: propLanding?.id,
        goalEntryId: pickFirstGoalId(propGoalEntries)
    });

    const resultRef = useRef(null);
    const staticNames = t?.constructors || {};

    const getMetaParams = useCallback(() => {
        let metaParams = Object.keys(state.metaParams).map((paramKey) => {
            let value = state.metaParams[paramKey];
            return value ? paramKey + '=' + value : ''
        });

        metaParams = metaParams.filter(n => {
            return n && n !== '&'
        });
        return metaParams.length ? '&' + metaParams.join('&') : '';
    }, [state.metaParams]);

    const resolveCurrentLink = useCallback(() => {
        if (state.currentLink?.id) {
            return state.currentLink;
        }
        if (Array.isArray(state.links) && state.links.length) {
            return state.links[0];
        }
        if (state.offer?.default_link) {
            return state.offer.default_link;
        }
        return null;
    }, [state.currentLink, state.links, state.offer]);

    const getLinkParams = useCallback(() => {
        const resolvedLink = resolveCurrentLink();
        if (!resolvedLink || !resolvedLink.id) {
            return '';
        }
        const linkIdParam = '?l=' + resolvedLink.id;
        const selfieLink = state.selfieLink;

        return (selfieLink && resolvedLink.selfie_link)
            ? linkIdParam + '&sl=' + encodeURIComponent(selfieLink)
            : linkIdParam;
    }, [resolveCurrentLink, state.selfieLink]);

    const getResultLink = useCallback((promo, publisherIsReferral = false) => {
        let metaParams = getMetaParams();

        if (promo && promo.params) {
            let promoParams = typeof promo.params === "string" ? JSON.parse(promo.params) : promo.params;

            if (promoParams !== undefined && promoParams !== null) {
                Object.keys(promoParams).forEach((key) => {
                    const value = promoParams[key];
                    if (value && value.length > 0) {
                        metaParams = metaParams + '&' + key + '=' + value;
                    }
                });
            }
        }

        const goalEntryId = state.goalEntryId || pickFirstGoalId(state.goalEntries);
        if (!goalEntryId || !state.publisherId || !state.offer?.tds_domain) {
            return '';
        }

        let advertiserIsReferral = false,
            offerIsReferral = false,
            currentGoalEntryId = goalEntryId;
        if (state.goalEntries.length) {
            state.goalEntries.forEach(function (goalEntry) {
                if (goalEntry.id === currentGoalEntryId) {
                    advertiserIsReferral = goalEntry.offer.advertiser.referral_advertiser;
                    offerIsReferral = goalEntry.offer.referral_offer;
                }
            });
        }

        metaParams = metaParams + '&creative_type=' + state.creativeType + '&creative_id=' + state.creativeId;

        if (publisherIsReferral && advertiserIsReferral && offerIsReferral) {
            metaParams += '&param8=referer_id';
        }

        const linkParams = getLinkParams();
        if (!linkParams) {
            return '';
        }

        const domain = state.offer?.tds_domain || '';

        return `${domain}/${state.publisherId}/${goalEntryId}${linkParams}${metaParams}`;
    }, [state.goalEntryId, state.goalEntries, state.publisherId, state.offer, getMetaParams, getLinkParams]);

    const getUrl = useCallback((landing) => {
        if (landing?.is_external && landing?.external_url) {
            return `${landing.external_url}/index.html`;
        }

        if (!landing?.url) {
            return '';
        }

        let landingDomain = config.landingUrl || '';
        const offerTds = propOffer?.tds_domain;
        const offerLandingsDomain = propOffer?.landings_domain;

        if (offerTds && config.dnsMap[offerTds]) {
            landingDomain = config.dnsMap[offerTds];
        } else if (offerLandingsDomain) {
            landingDomain = offerLandingsDomain;
        } else if (offerTds && offerTds.includes('tds.')) {
            landingDomain = offerTds.replace('tds.', 'landing.');
        } else if (!landingDomain && offerTds) {
            landingDomain = offerTds;
        }

        if (!landingDomain) {
            landingDomain = window?.location?.origin || '';
        }

        if (!landingDomain) {
            return landing.url;
        }

        return landing.url.replace(/(.*?)\/public\//giu, `${landingDomain}/public/`);
    }, [propOffer]);

    const getCode = useCallback((landing) => {
        const baseUrl = getUrl(landing);
        if (!baseUrl) {
            return '';
        }
        const redirectUrl = getResultLink(landing, state.publisherIsReferral);
        if (!redirectUrl) {
            return '';
        }

        return `${baseUrl}?redirectUrl=${redirectUrl}`;
    }, [getUrl, getResultLink, state.publisherIsReferral]);

    const getAllowChar = useCallback(() => {
        return /[^\da-zA-Z.%_{}\[\]#$\-]/g;
    }, []);

    const onErrorMsg = useCallback((key) => {
        CustomNotify((t && t.constructors && t.constructors[key]) || '', 'error');
    }, [t]);

    const setParam = useCallback((e) => {
        if (e.target.value.replace(getAllowChar(), '') !== e.target.value) {
            onErrorMsg('notAllowCharMsg');
            return false;
        }

        const key = e.target.placeholder;
        const value = e.target.value;

        setState(prevState => {
            const nextMeta = {...(prevState.metaParams || {})};
            const nextLanding = {...(prevState.landing || {}), params: {...(prevState.landing?.params || {})}};

            nextMeta[key] = value;

            if (Object.prototype.hasOwnProperty.call(prevState.landing?.params || {}, key)) {
                nextLanding.params[key] = value;
            }

            return {
                ...prevState,
                metaParams: nextMeta,
                landing: nextLanding
            };
        });
    }, [getAllowChar, onErrorMsg, state.landing]);

    const onLinkChange = useCallback((linkId) => {
        setState(prevState => ({
            ...prevState,
            currentLink: (prevState.links || []).find(l => String(l.id) === String(linkId)) || {}
        }));
    }, []);

    const setSelfieLink = useCallback((e) => {
        setState(prevState => ({
            ...prevState,
            selfieLink: e.target.value
        }));
    }, []);

    const onGoalChange = useCallback((e) => {
        setState(prevState => ({
            ...prevState,
            goalEntryId: e.target.value
        }));
    }, []);

    const onToggleExternalHosting = useCallback((e) => {
        const checked = !!e.target.checked;
        setState(prevState => ({
            ...prevState,
            landing: {
                ...(prevState.landing || {}),
                is_external: checked,
                external_url: checked ? (prevState.landing?.external_url || '') : ''
            }
        }));
    }, []);

    const onChangeExternalLink = useCallback((e) => {
        const value = e.target.value;
        setState(prevState => ({
            ...prevState,
            landing: {
                ...(prevState.landing || {}),
                external_url: value
            }
        }));
    }, []);

    const onCopyButtonClick = useCallback(() => {
        const preparedState = {
            ...state,
            landing: {
                ...state.landing,
                is_external: !!state.landing?.is_external,
                external_url: state.landing?.external_url || state.externalLink || ''
            }
        };

        ClipboardCopy.copy(resultRef.current);
        CustomNotify((t && t.constructors && t.constructors.copyLandingLinkBuffer) || '');

        propSaveLanding(preparedState, t || {}, () => {
        }, () => {
        });
    }, [state, t, propSaveLanding]);

    const getStatusIcon = useCallback(() => {
        const dataGoal = state.goalEntries.filter((item) => item.id === state.goalEntryId)[0] || {};

        return dataGoal.status === statuses.paused
            ? (
                <CustomTooltip id={`icon-paused-constructor`}
                               tooltip={(t && t.offersPage && t.offersPage.offer && t.offersPage.offer.paused) || ''}>
                    <Icon name={`link-construrtor__label-icon fa fa-pause-circle`}/>
                </CustomTooltip>
            )
            : null;
    }, [state.goalEntries, state.goalEntryId, t]);

    const renderGoalEntries = useCallback(() => {
        return (
            <div className="form-group">
                <label className="control-label">{(t && t.constructors && t.constructors.goal) || ''}</label>
                {getStatusIcon()}
                <select onChange={onGoalChange}
                        value={state.goalEntryId ? String(state.goalEntryId) : ''}
                        className="form-control"
                >
                    {
                        state.goalEntries.map((goal, key) => {
                            return <option key={key} disabled={!goal.access_granted}
                                           value={String(goal.id)}>{goal.goal.title}</option>
                        })
                    }
                </select>
            </div>
        );
    }, [t, getStatusIcon, onGoalChange, state.goalEntryId, state.goalEntries]);

    const renderOfferLinks = useCallback(() => {
        return (
            <FormGroup
                label={staticNames.link}
            >
                <select onChange={(e) => onLinkChange(e.target.value)}
                        value={state.currentLink?.id ? String(state.currentLink.id) : ((state.links && state.links[0]?.id) ? String(state.links[0].id) : '')}
                        className="form-control"
                >
                    {
                        (state.links || []).map((link, key) => {
                            return <option key={key} value={String(link.id)}>{link.title}</option>
                        })
                    }
                </select>
            </FormGroup>
        );
    }, [staticNames, state.links, state.currentLink, onLinkChange]);

    const renderLandingParams = useCallback(() => {
        const utmKeys = metaUtmParams || [];
        const subKeys = Array.from(Array(config.metaParamsCount).keys()).map((i) => 'param' + (i + 1));
        const keys = Object.keys(state.landing.params || {}).filter(k => utmKeys.indexOf(k) === -1 && subKeys.indexOf(k) === -1);
        return (
            <div className="form-group">
                <label className="control-label">{(t && t.constructors && t.constructors.landingParams) || ''}</label>
                <div className="form-row">
                    {keys.map(key => {
                        let value = state.metaParams[key] || '';
                        return (
                            <input
                                type="text"
                                maxLength="50"
                                onChange={setParam}
                                placeholder={key}
                                className="form-control"
                                value={value}
                                key={key}
                            />
                        )
                    })}
                </div>
            </div>
        );
    }, [t, state.landing.params, state.metaParams, setParam]);

    const renderParams = useCallback((promoType) => {
        return (
            <div className="form-group">
                <label className="control-label bold">{(t && t.constructors && t.constructors.utm) || ''}</label>
                <div className="form-row">
                    {metaUtmParams.map(key => {
                        let value = state.metaParams[key] || '';
                        return (
                            <input
                                type="text"
                                maxLength="50"
                                onChange={setParam}
                                placeholder={key}
                                className="form-control"
                                value={value}
                                key={key}
                            />
                        )
                    })}
                </div>
            </div>
        );
    }, [t, state.metaParams, setParam]);

    const renderSubMetaParams = useCallback((promoType) => {
        return (
            <div className="form-group">
                <label className="control-label bold">{(t && t.constructors && t.constructors.subaccount) || ''}</label>
                <div className="form-row">
                    {Array.from(Array(config.metaParamsCount).keys()).map((index) => {
                        let value = state.metaParams['param' + (index + 1)] || '';
                        return (
                            <input type="text" maxLength="50"
                                   onChange={setParam}
                                   placeholder={'param' + (index + 1)}
                                   className="form-control"
                                   value={value}
                                   key={index}
                            />
                        )
                    })}
                </div>
            </div>
        )
    }, [t, state.metaParams, setParam]);

    const renderSelfieLink = useCallback(() => {
        return (
            <React.Fragment>
                <div className="hr-line-dashed"/>
                <FormGroup
                    label={'Selfie link'}
                >
                    <input
                        type="text"
                        maxLength="255"
                        value={state.selfieLink}
                        onChange={setSelfieLink}
                        placeholder="Selfie link"
                        className="form-control"
                    />
                </FormGroup>
            </React.Fragment>
        );
    }, [state.selfieLink, setSelfieLink]);

    useEffect(() => {
        const isChangeOffer = state.offer !== propOffer;
        const isChangeLanding = state.landing !== propLanding;
        const isChangeLinks = state.links !== propLinks;
        const nextGoalId = pickFirstGoalId(propGoalEntries);

        setState(prevState => {
            const updatedState = {
                ...prevState,
                creativeType: 'landing',
                creativeId: propLanding?.id,
                visible: propVisible,
                landing: propLanding,
                goalEntries: propGoalEntries,
                links: Array.isArray(propLinks) ? propLinks : [],
                offer: propOffer
            };

            if (nextGoalId) {
                updatedState.goalEntryId = nextGoalId;
            }

            if (isChangeOffer || isChangeLanding || isChangeLinks) {
                updatedState.selfieLink = '';
                updatedState.metaParams = {};
                updatedState.currentLink = propOffer?.default_link || (Array.isArray(propLinks) ? propLinks[0] : null) || {};
            }

            return updatedState;
        });
    }, [propVisible, propLanding, propLinks, propGoalEntries, propOffer]);

    if (!state.links && state.visible) {
        CustomNotify(staticNames.cantCreateUrlForCreativeNoLinksAvailable, 'error');
        return '';
    }

    return (
        <CustomModal
            className="create-landing-link-modal"
            title={staticNames.createLandingLink}
            confirmName={staticNames.copyLandingLink}
            visible={state.visible}
            onClose={propOnClose}
            onConfirm={onCopyButtonClick}
        >
            {renderGoalEntries()}

            {renderOfferLinks()}

            <label className="checkbox-inline option">
                <input
                    type="checkbox"
                    className="option__input js-external"
                    name="type"
                    value="true"
                    onChange={onToggleExternalHosting}
                    checked={!!state.landing?.is_external}
                />
                <span className="option__mark"/>
                <span
                    className="option__caption">{(t && t.constructors && t.constructors.externalHosting) || 'External hosting'}</span>
            </label>
            <FormGroup>
                <input
                    className="form-control external_url"
                    type="text"
                        readOnly={!state.landing?.is_external}
                    required={false}
                    placeholder={(t && t.constructors && t.constructors.externalLandingLink) || 'External URL'}
                    style={{minHeight: 38, maxHeight: 38}}
                    onChange={onChangeExternalLink}
                    value={state.landing?.is_external ? (state.landing?.external_url || '') : ''}
                />
            </FormGroup>

            {/*{renderLandingParams()}*/}

            {renderParams('landing')}

            {renderSubMetaParams('landing')}
            {state.currentLink.selfie_link
                ? renderSelfieLink()
                : null
            }

            <FormGroup
                label={staticNames.resultLandingLink}
            >
                    <textarea
                        className="form-control"
                        readOnly={true}
                        value={getCode(state.landing || propLanding)}
                        ref={resultRef}
                        style={{maxHeight: 100}}
                    />
            </FormGroup>
        </CustomModal>
    );
};

ConstructorLandings.propTypes = {
    visible: PropTypes.bool,
    landing: PropTypes.object,
    offer: PropTypes.object,
    goalEntries: PropTypes.array,
    publisherId: PropTypes.string,
    publisherIsReferral: PropTypes.bool,
    links: PropTypes.array,
    saveLanding: PropTypes.func,
    onClose: PropTypes.func,
    t: PropTypes.object
};

ConstructorLandings.defaultProps = {
    visible: false,
    landing: {},
    offer: {},
    goalEntries: [],
    publisherId: '',
    publisherIsReferral: false,
    links: [],
    saveLanding: () => {
    },
    onClose: () => {
    }
};

export default withTranslation(ConstructorLandings);
